import Immutable from 'immutable';
import { createSelector } from '@reduxjs/toolkit';

import type { Project, TeamInvitation, User, Workspace, WorkspaceProject } from 'js/src/client';
import {
    DefaultDashboardViewEnum,
    RoleEnum,
    UserPermissions,
    VideoVisibility,
} from 'js/src/client';
import { getWorkspaceSlugFromPath } from 'js/src/data/account/utils';
import StoreORM from 'js/src/data/store-orm';
import { getServerData } from 'js/src/libs/server-data';
import type { ImmutableMap } from 'js/src/libs/consts';
import type { RootState } from 'js/src/libs/RootState';
import Cookies from 'js-cookie';

/* SELECTORS  */
const EMPTY_LIST = Immutable.List();

export function isMemberInWorkspace(
    member: ImmutableMap<User>,
    workspace: ImmutableMap<Workspace>,
) {
    if (!member || !workspace) {
        return false;
    }
    return member.get('workspaces').includes(workspace.get('id'));
}

export const getAccountWorkspaces = (state: RootState) => state.account.workspaces;

/**
 * selector for workspace IDs, and if filterByMember == true, then list of workspace IDs user is a member of
 * @param {StoreORM} state.account.workspaces
 * @param {boolean} filterByMember - flag to filter workspaceIds by whether user is a member of
 * @returns {Immutable.List<string>} - list of workspace IDs
 *
 * reselect input selectors receives arguments for all input selectors, so the 2nd input selector is just
 * for the argument to pass through to the transform function
 * https://redux.js.org/usage/deriving-data-selectors#createselector-behavior
 */
export const selectAllWorkspaceOrmIds = createSelector(
    (state: RootState) => state.account.workspaces,
    (state: RootState) => state.account.team_members,
    (state: RootState, filterByMember: boolean = true) => filterByMember, // argument pass-through
    (workspaces, team_members, filterByMember: boolean) => {
        const workspacesOrm = new StoreORM<Workspace>(workspaces);

        let workspaceIds: Immutable.List<number>;
        if (filterByMember) {
            const teamMembersOrm = new StoreORM<User>(team_members);
            const userId = getServerData()?.USER?.id;
            const user =
                userId === undefined ? undefined : teamMembersOrm.getByIdOrUndefined(userId);
            if (!user) {
                return EMPTY_LIST as Immutable.List<number>;
            }
            workspaceIds = user.get('workspaces');
        } else {
            workspaceIds = workspacesOrm.getAllIds();
        }

        // Sort workspace IDs alphabetically by workspace name
        return workspaceIds.sort((a, b) => {
            const nameA = workspacesOrm.getByIdOrUndefined(a)?.get('name') || '';
            const nameB = workspacesOrm.getByIdOrUndefined(b)?.get('name') || '';
            return nameA.localeCompare(nameB);
        });
    },
);

export const getAllWorkspaceInfoList = createSelector(
    (state: RootState) => state.account.workspaces,
    (workspaces) => {
        if (!workspaces) {
            return EMPTY_LIST as Immutable.List<ImmutableMap<Workspace>>;
        }
        const orm = new StoreORM<Workspace>(workspaces);
        return orm.map((workspaceInfo) => workspaceInfo);
    },
);

/**
 * selector to check if any workspaces have editor advanced settings enabled
 * @param {StoreORM} state.account.workspaces
 * @returns {Boolean} - True if any workspaces have editor advanced settings enabled
 */
export const checkAnyWorkspacesWithEditorAdvancedSettingsOn = createSelector(
    (state: RootState) => state.account.workspaces,
    (workspaces) => {
        if (!workspaces) {
            return false;
        }
        const orm = new StoreORM<Workspace>(workspaces);
        return (
            orm.filter(
                (workspaceInfo) => workspaceInfo.get('enable_editor_advanced_settings') ?? false,
            ).size > 0
        );
    },
);

/**
 * selector to get workspaces where current user is a member of
 * @param {StoreORM} state.account.workspaces
 * @returns {Immutable.List<Immutable.Map<Workspaces>>} - returns workspace info where current user is a member of
 */
export const getMemberWorkspaceInfoList = createSelector(
    (state: RootState) => state.account.workspaces,
    (state: RootState) => state.account.team_members,
    (workspaces, team_members): Immutable.List<ImmutableMap<Workspace>> => {
        if (!team_members) {
            return EMPTY_LIST as Immutable.List<ImmutableMap<Workspace>>;
        }
        const teamMembersOrm = new StoreORM<User>(team_members);
        const userId = getServerData()?.USER?.id;
        const user = userId === undefined ? undefined : teamMembersOrm.getByIdOrUndefined(userId);
        if (!user) {
            return EMPTY_LIST as Immutable.List<ImmutableMap<Workspace>>;
        }
        if (!workspaces) {
            return EMPTY_LIST as Immutable.List<ImmutableMap<Workspace>>;
        }
        const orm = new StoreORM<Workspace>(workspaces);
        return orm.filter((workspaceInfo) =>
            user.get('workspaces').includes(workspaceInfo.get('id')),
        );
    },
);

export function getAllProjectIds(state: RootState): Immutable.List<number> {
    if (!state.account || !state.account.projects) {
        return EMPTY_LIST as Immutable.List<number>;
    }

    const orm = new StoreORM<Project>(state.account.projects);
    return orm.getAllIds();
}

export const getAllProjects = createSelector(
    (state: RootState) => state.account.projects,
    (projects): Immutable.List<ImmutableMap<Project>> => {
        if (!projects) {
            return EMPTY_LIST as Immutable.List<ImmutableMap<Project>>;
        }
        const orm = new StoreORM<Project>(projects);
        return orm.filter((project) => true);
    },
);

export function getProjectById(state: RootState, project_id: number) {
    const orm = new StoreORM<Project>(state.account.projects);
    return orm.getByIdOrUndefined(project_id);
}

export const selectProjectsInWorkspace = createSelector(
    (state: RootState) => state.account.workspaces,
    (state: RootState, workspaceId: number | null) => workspaceId,
    (workspaces, workspaceId: number | null) => {
        if (workspaceId === null) {
            return EMPTY_LIST as Immutable.List<ImmutableMap<WorkspaceProject>>;
        }
        const workspacesOrm = new StoreORM<Workspace>(workspaces);
        return (
            workspacesOrm.getByIdOrUndefined(workspaceId)?.get('project_details') ??
            (EMPTY_LIST as Immutable.List<ImmutableMap<WorkspaceProject>>)
        );
    },
);

export const selectProjectIdsInWorkspace = createSelector(
    (state: RootState) => state.account.projects,
    (state: RootState, workspaceId: number | null) => workspaceId,
    (projects, workspaceId: number | null) => {
        if (workspaceId === null) {
            return EMPTY_LIST as Immutable.List<number>;
        }
        const projectsOrm = new StoreORM<Project>(projects);
        return projectsOrm
            .filter((project) => {
                return project.get('workspace') === workspaceId;
            })
            .map((project) => project.get('id'));
    },
);

export function selectActiveWorkspace(state: RootState): ImmutableMap<Workspace> | null {
    const orm = new StoreORM<Workspace>(state.account.workspaces);
    if (getServerData()?.USER?.workspace_id) {
        const workspace = orm
            .query({
                key: ['id'],
                value: getServerData()?.USER?.workspace_id,
            })
            .get(0);
        if (workspace) {
            return workspace;
        }
    }
    return null;
}

export function getActiveWorkspaceMembers(state: RootState) {
    const workspace = selectActiveWorkspace(state);

    if (!workspace) {
        return Immutable.List<ImmutableMap<User>>();
    }
    return selectAllTeamMembersInWorkspace(state, workspace.get('id'));
}

export function selectActiveWorkspaceName(state: RootState): string {
    const workspace = selectActiveWorkspace(state);

    if (workspace) {
        /**
         * As user can manually change the workspace slug via the url,
         * SERVER.USER.workspace_id is not updated in that case.
         * Thus we check if the slug matches any logged-in user workspace slugs
         * and update the dropdown selection correctly.
         *  */
        const activeUserWorkspaceViaUrl = selectWorkspaceFromSlug(state, false);
        if (activeUserWorkspaceViaUrl && !activeUserWorkspaceViaUrl.equals(workspace)) {
            return activeUserWorkspaceViaUrl.get('name') || '';
        } else {
            return workspace.get('name') || '';
        }
    }

    return '';
}

export const getWorkspaceNameById = (workspaceId: number) => (state: RootState) => {
    const workspace = selectWorkspaceByOrmId(state, workspaceId);
    if (!workspace) return '';
    return getNameFromWorkspace(workspace);
};

export function getNameFromWorkspace(workspace: ImmutableMap<Workspace> | undefined) {
    if (!workspace) {
        return '';
    }
    return workspace.get('name') || '';
}

export function getWorkspaceEnabledEditorAdvancedSettingsById(
    state: RootState,
    workspaceId: number | null,
) {
    const workspace = selectWorkspaceByOrmId(state, workspaceId);
    if (!workspace) return null;
    return getAdvancedSettingsEnabledFromWorkspace(workspace);
}

export function getAdvancedSettingsEnabledFromWorkspace(
    workspace: ImmutableMap<Workspace> | undefined,
) {
    if (!workspace) {
        return false;
    }
    return workspace.get('enable_editor_advanced_settings');
}

export function selectWorkspaceByOrmId(state: RootState, workspace_orm_id: number | null) {
    const orm = new StoreORM<Workspace>(state.account.workspaces);
    if (workspace_orm_id === null) {
        return undefined;
    }
    return orm.getByIdOrUndefined(workspace_orm_id);
}

export const getWorkspaceSlugById = (workspaceId: number) => (state: RootState) => {
    const workspace = selectWorkspaceByOrmId(state, workspaceId);
    return workspace?.get('slug') || '';
};

export const getWorkspaceFeedSecret = (workspaceId: number) => (state: RootState) => {
    const workspace = selectWorkspaceByOrmId(state, workspaceId);
    return workspace?.get('feed_secret') || '';
};

export const getNamesOfWorkspacesUserHasAccessTo = createSelector(
    (state: RootState) => state.account.workspaces,
    (state: RootState, teamMember: ImmutableMap<User> | null) => teamMember,
    (workspaces, teamMember) => {
        if (!teamMember) {
            return Immutable.Map<number, string>();
        }
        const workspacesOrm = new StoreORM<Workspace>(workspaces);
        return Immutable.Map<number, string>(
            teamMember
                .get('workspaces')
                .map((workspaceId: number) => [
                    workspaceId,
                    getNameFromWorkspace(workspacesOrm.getByIdOrUndefined(workspaceId)),
                ]),
        );
    },
);

export const checkAdvancedSettingsEnabledForWorkspacesUserHasAccessTo = createSelector(
    (state: RootState) => state.account.workspaces,
    (state: RootState, teamMember: ImmutableMap<User> | null) => teamMember,
    (workspaces, teamMember) => {
        if (!teamMember) {
            return Immutable.Map<number, boolean | null>();
        }
        const workspacesOrm = new StoreORM<Workspace>(workspaces);
        return Immutable.Map(
            teamMember
                .get('workspaces')
                .map((workspaceId: number) => [
                    workspaceId,
                    getAdvancedSettingsEnabledFromWorkspace(
                        workspacesOrm.getByIdOrUndefined(workspaceId),
                    ),
                ]),
        );
    },
);

/**
 * selector to get default visibility of a workspace
 * @param {state} - state to select state.account.workspaces
 * @param {workspaceId} - id of workspace we want to get default_visibility out of
 * @returns {string} - one of VideoVisibility
 */
export const selectDefaultWorkspaceVisibilityByWorkspaceId = createSelector(
    (state: RootState) => state.account.workspaces, // direct lookup for workspaces
    (state: RootState, workspaceId: number) => workspaceId, // argument pass-through for transform function
    (workspaces, workspaceId: number) => {
        const workspacesOrm = new StoreORM<Workspace>(workspaces);
        const workspace = workspacesOrm.getById(workspaceId);
        return workspace.get('default_visibility') || VideoVisibility.UNLISTED;
    },
);

export function selectWorkspaceFromSlug(state: RootState, navigationRequired = true) {
    const slug = getWorkspaceSlugFromPath(navigationRequired);
    const orm = new StoreORM<Workspace>(state.account.workspaces);
    return orm
        .query({
            key: ['slug'],
            value: slug,
        })
        .get(0);
}

export function selectWorkspaceByWorkspaceId(state: RootState, id: number) {
    const orm = new StoreORM<Workspace>(state.account.workspaces);
    return orm.getById(id);
}

export function selectAllTeamMemberOrmIds(state: RootState) {
    const orm = new StoreORM<User>(state.account.team_members);
    return orm.getAllIds();
}

export function selectAllTeamMembersInWorkspace(state: RootState, workspaceId: number) {
    const orm = new StoreORM<User>(state.account.team_members);
    return orm.filter((teamMember) => {
        return teamMember.get('workspaces').includes(workspaceId);
    });
}

export function selectAllAdminTeamMembers(
    state: RootState,
): Immutable.List<ImmutableMap<User>> | null {
    const orm = new StoreORM<User>(state.account.team_members);
    if (orm.getAllIds().size === 0) {
        // haven't loaded yet
        return null;
    }
    return orm.query({
        key: ['role'],
        value: RoleEnum.ADMIN,
    });
}

export function selectTeamMemberByOrmId(state: RootState, team_member_orm_id: number) {
    const orm = new StoreORM<User>(state.account.team_members);
    return orm.getById(team_member_orm_id);
}

export function getAllTeamMembers(state: RootState) {
    const orm = new StoreORM<User>(state.account.team_members);
    return orm.filter((teamMember) => true);
}

/**
 * @param {RootState} state - rootState
 * @returns int of team size
 */
export const getTeamSize = (state: RootState) => {
    const orm = new StoreORM<User>(state.account.team_members);
    return orm.length;
};

/**
 * selects all team members that belong to a specific workspace
 * @param {RootState} state - root state to look-up workspaces and team_members states
 * @param {string} workspaceId - id of specific workspace you want team members of
 * @returns <Immutable.List> - list of team members within this specific workspace
 */
export const selectTeamMembersInWorkspace = createSelector(
    (state: RootState) => state.account.workspaces, // direct lookup to workspaces
    (state: RootState) => state.account.team_members, // direct lookup to team
    (state: RootState, workspaceId: number) => workspaceId, // argument passthrough to transform function
    (workspaces, team, workspaceId: number) => {
        const team_orm = new StoreORM<User>(team);
        const workspacesOrm = new StoreORM<Workspace>(workspaces);
        const workspace = workspacesOrm.getByIdOrUndefined(workspaceId);

        let members_in_workspace = Immutable.List<ImmutableMap<User>>();
        if (workspace) {
            team_orm.forEach((member) => {
                if (isMemberInWorkspace(member, workspace)) {
                    members_in_workspace = members_in_workspace.push(member);
                }
            });
        }
        return members_in_workspace;
    },
);

/**
 * selects users that are on the same team but not within the specified workspace
 * @param {RootState} state - root state to look-up workspaces and team_members states
 * @param {string} workspaceId - id of specific workspace
 * @returns <Immutable.List> - list of team members not in this workspace
 */
export const selectTeamMembersNotInWorkspace = createSelector(
    (state: RootState) => state.account.workspaces,
    (state: RootState) => state.account.team_members,
    (state: RootState, workspaceId: number) => workspaceId,
    (workspaces, team, workspaceId: number) => {
        const team_orm = new StoreORM<User>(team);
        const workspacesOrm = new StoreORM<Workspace>(workspaces);
        const workspace = workspacesOrm.getById(workspaceId);

        let members_not_in_workspace = Immutable.List<ImmutableMap<User>>();
        team_orm.forEach((member) => {
            if (
                member.get('id') !== getServerData()?.USER?.id &&
                !isMemberInWorkspace(member, workspace)
            ) {
                members_not_in_workspace = members_not_in_workspace.push(member);
            }
        });

        return members_not_in_workspace;
    },
);

export const hasUserWithOnlyThisWorkspace = createSelector(
    (state: RootState) => state.account.team_members,
    (state: RootState, workspaceId: number) => workspaceId,
    (team, workspaceId: number) => {
        const teamOrm = new StoreORM<User>(team);
        return (
            teamOrm.filter((member) => {
                const workspaces = member.get('workspaces');
                return workspaces && workspaces.includes(workspaceId) && workspaces.size === 1;
            }).size > 0
        );
    },
);

export const selectUsersWithAccessToWorkspaceAndProject = createSelector(
    (state: RootState) => state.account.team_members,
    (state: RootState) => state.account.workspaces,
    (state: RootState, workspaceId: number | undefined) => workspaceId,
    (state: RootState, workspaceId: number | undefined, projectId: number | null) => projectId,
    (state: RootState) => state.account.projects,
    (allTeamMembers, workspaces, workspaceId, projectId, projects) => {
        if (workspaceId === undefined) {
            return Immutable.List<ImmutableMap<User>>();
        }
        // ADMINS
        const allTeamMembersOrm = new StoreORM<User>(allTeamMembers);
        if (allTeamMembersOrm.getAllIds().size === 0) {
            return Immutable.List<ImmutableMap<User>>();
        }
        const workspaceOrm = new StoreORM<Workspace>(workspaces);
        const currentWorkspace = workspaceOrm.getByIdOrUndefined(workspaceId);
        if (!currentWorkspace) {
            return Immutable.List<ImmutableMap<User>>();
        }

        // access = workspace admins + project users
        const admins = allTeamMembersOrm.filter((member) => {
            return (
                member.get('workspaces').includes(workspaceId) &&
                member.get('role') === RoleEnum.ADMIN
            );
        });

        // PROJECT USERS
        let projectUsers = Immutable.List<ImmutableMap<User>>();
        if (projectId !== null) {
            const projectOrm = new StoreORM<Project>(projects);
            const currentProject = projectOrm.getByIdOrUndefined(projectId);
            if (currentProject) {
                projectUsers = allTeamMembersOrm.filter((member) => {
                    return member.get('projects').includes(projectId);
                });
            }
        }
        return admins.concat(projectUsers).toSet().toList();
    },
);

export function selectWorkspaceTeamSize(state: RootState, workspaceId: number) {
    return selectTeamMembersInWorkspace(state, workspaceId).size;
}

export function selectUserProjects(state: RootState) {
    return state.account?.user_profile?.user_projects;
}

export function getUserId(state: RootState) {
    return state.account.user_profile.user_id;
}

export function getFirstName(state: RootState) {
    return state.account.user_profile.first_name;
}

export function getLastName(state: RootState) {
    return state.account.user_profile.last_name;
}

export function selectDashboardName(state: RootState) {
    return state.account.user_profile.first_name || state.account.user_profile.email;
}

export function canSeeVideoControls(state: RootState) {
    // Backend-authoritative: current_user_can_download is the DownloadVideo scoped-access rule
    // plus a VIEWER exclusion -- showControls gates writes (Edit, rename, visibility) too, so a
    // read-only VIEWER is excluded here while keeping their DownloadVideo.get download.
    return Boolean(state.shareVideo.video?.current_user_can_download);
}

export function getProjectsLoadingIndicator(state: RootState) {
    return state.account.projects_loading_indicator;
}

export function select2FAActivated(state: RootState) {
    return state.account.user_profile.two_factor_activated;
}

export function getTeamMFASettingFlag(state: RootState) {
    return state.account.team_settings.mfa_required;
}

export function getDateJoinedCycleStartDate(state: RootState) {
    return state.account.team_settings.date_joined_cycle_start_date;
}

export function getDateJoinedCycleEndDate(state: RootState) {
    return state.account.team_settings.date_joined_cycle_end_date;
}

export function getVideosCreatedInCycle(state: RootState) {
    return state.account.team_settings.videos_created_in_cycle;
}

export function getMaxVideosInCycle(state: RootState) {
    return state.account.team_settings.max_videos_in_cycle;
}

export function getMaxVideoDurationSeconds(state: RootState) {
    return state.account.team_settings.max_video_duration_seconds;
}

export function getCanUseAIVoiceover(state: RootState): boolean {
    return state.account.team_settings.can_use_ai_voiceover_flow;
}

export function getCanUseAIScript(state: RootState) {
    return state.account.team_settings.can_use_ai_script_flow;
}

export function getCanUseOutlineAnIdea(state: RootState): boolean {
    return state.account.team_settings.can_use_outline_an_idea;
}

export function getCanUsePremiumAIVoices(state: RootState): boolean {
    return state.account.team_settings.can_use_premium_ai_voices;
}

export function getCanUseEditorialMedia(state: RootState): boolean {
    return state.account.team_settings.can_use_editorial_media;
}

export function getCanUseShutterstockUnlimitedMedia(state: RootState): boolean {
    return state.account.team_settings.can_use_shutterstock_unlimited_media;
}

export function getCanUseAIAvatar(state: RootState): boolean {
    return state.account.team_settings.can_use_ai_avatar;
}

export function getCanPublishAIAvatar(state: RootState): boolean {
    return state.account.team_settings.can_publish_ai_avatar;
}

export function getCanUseAIMedia(state: RootState): boolean {
    return state.account.team_settings.can_use_ai_media;
}

export function getCanUseAnimatedExplainer(state: RootState): boolean {
    return state.account.team_settings.can_use_animated_explainer;
}

// team_settings is fetched asynchronously, so its fields hold reducer defaults until the
// fetch resolves. date_joined_cycle_start_date is always populated by storeTeamSettings and
// is null until then, making it a reliable "settings have loaded" signal. Consumers that act
// on a capability flag (e.g. redirecting on !getCanUseGenX) must wait for this to avoid acting
// on default values before the real ones arrive.
export function getTeamSettingsLoaded(state: RootState): boolean {
    return state.account.team_settings.date_joined_cycle_start_date !== null;
}

// GenX (animated explainers) relies on AI voiceover, script generation, and AI media, and can
// also be disabled outright for a team via can_use_animated_explainer.
// A team that has opted out of any one of these cannot use GenX.
//
// Returns null until team settings have loaded — the capability flags hold reducer defaults
// before then, so distinguishing "not yet known" from a real false matters. Callers must
// branch on `=== false` (definitely blocked) vs `=== true` (definitely allowed) rather than
// truthiness, so they never act on the unknown state (e.g. redirecting before the fetch).
export function getCanUseGenX(state: RootState): boolean | null {
    if (!getTeamSettingsLoaded(state)) return null;
    return Boolean(
        getCanUseAIVoiceover(state) &&
        getCanUseAIScript(state) &&
        getCanUseAIMedia(state) &&
        getCanUseAnimatedExplainer(state),
    );
}

export function getCanUseSketch(state: RootState): boolean {
    return state.account.team_settings.can_use_sketch;
}

export function getCanUseScreenRecording(state: RootState): boolean {
    return state.account.team_settings.can_use_screen_recording;
}

export function isVideoLimitExceededInCycle(state: RootState) {
    const videosCreated = getVideosCreatedInCycle(state);
    const videosAllowed = getMaxVideosInCycle(state);
    if (typeof videosCreated !== 'number' || typeof videosAllowed !== 'number') {
        // the limit is null or undefined, so there is no limit!
        return false;
    }
    return videosCreated >= videosAllowed;
}

export function getTeamVideoApprovalFlag(state: RootState): boolean {
    return state.account.team_settings.can_request_video_approval;
}

export function getTeamHasAuditLog(state: RootState): boolean {
    return state.account.team_settings.has_audit_log;
}

export function isApprovalWorkflowRestricted(state: RootState): boolean | null {
    const activeWorkspace = selectActiveWorkspace(state);
    if (activeWorkspace) {
        return (
            activeWorkspace.get('editors_require_approval_to_publish') === true &&
            getTeamVideoApprovalFlag(state)
        );
    } else {
        return null;
    }
}

export function getUserPermissions(state: RootState, userId: number): UserPermissions[] {
    if (getServerData()?.USER?.id === userId) {
        return state.account.user_profile.permissions;
    }

    const orm = new StoreORM<User>(state.account.team_members);
    const teamMember = orm
        .query({
            key: ['id'],
            value: userId,
        })
        .get(0) as ImmutableMap<User>;

    if (!teamMember) {
        return EMPTY_LIST as unknown as UserPermissions[];
    }

    // for some reason (maybe should fix?) the teamMember is an immutable object
    // but the 'permissions' array is not
    return teamMember.get('permissions') as unknown as UserPermissions[];
}

export const getIsUserAllowedAccessToAccessBlueprintsAndBrandKits = (state: RootState): boolean => {
    /**
     * Return a boolean indicating if the user has access to blueprints and brand kits for the active workspace.
     * Because this controls visibility of tabs in the dashboard sidebar and depends on the workspace loading state,
     * we cache the value in a cookie to prevent flickering of the sidebar tabs while the workspace is loading.
     */
    const activeWorkspace = selectActiveWorkspace(state);

    const cookieVal = Cookies.get('access_saved_templates_and_brand_kits');
    if (!activeWorkspace) {
        // we haven't loaded workspaces yet, so unable to determine if user has access
        // fallback to cached value in Cookies (or default to false)
        return cookieVal === 'true';
    }

    let isAllowed = false;
    const isAdmin = getServerData()?.USER?.role === RoleEnum.ADMIN;
    if (isAdmin) {
        isAllowed = true;
    } else if (!activeWorkspace.get('enable_editor_advanced_settings')) {
        isAllowed = false;
    } else {
        const permissions = state.account.user_profile.permissions;
        isAllowed = permissions.includes(UserPermissions.ACCESS_SAVED_TEMPLATES_AND_BRAND_KITS);
    }
    // cache the value in a cookie
    if (cookieVal !== isAllowed.toString()) {
        Cookies.set('access_saved_templates_and_brand_kits', isAllowed.toString());
    }
    return isAllowed;
};

const isUserAllowedToUseAdvancedPermission = (
    state: RootState,
    acceptablePermissions: UserPermissions[],
) => {
    if (getServerData()?.USER?.role === RoleEnum.ADMIN) {
        return true;
    }

    const activeWorkspace = selectActiveWorkspace(state);
    if (activeWorkspace) {
        if (!activeWorkspace.get('enable_editor_advanced_settings')) {
            return false;
        }

        const permissions = state.account.user_profile.permissions;
        return acceptablePermissions.some((permission) => permissions.includes(permission));
    }
    return false;
};

export const getIsUserAllowedToManageProject = (state: RootState) => {
    return isUserAllowedToUseAdvancedPermission(state, [UserPermissions.CREATE_AND_MANAGE_PROJECT]);
};

export const getIsUserAllowedAccessToFonts = (state: RootState) => {
    return isUserAllowedToUseAdvancedPermission(state, [
        UserPermissions.ACCESS_SAVED_TEMPLATES_AND_BRAND_KITS,
        UserPermissions.ACCESS_FONTS,
    ]);
};

export const getIsUserAllowedAccessToWatermarks = (state: RootState) => {
    return isUserAllowedToUseAdvancedPermission(state, [
        UserPermissions.ACCESS_SAVED_TEMPLATES_AND_BRAND_KITS,
        UserPermissions.ACCESS_WATERMARKS,
    ]);
};

export const getIsUserAllowedAccessToColorPalettes = (state: RootState) => {
    return isUserAllowedToUseAdvancedPermission(state, [
        UserPermissions.ACCESS_SAVED_TEMPLATES_AND_BRAND_KITS,
        UserPermissions.ACCESS_COLOUR_PALETTES,
    ]);
};

export const getIsUserAllowedAccessToMoveAndResizeText = (state: RootState) => {
    return isUserAllowedToUseAdvancedPermission(state, [
        UserPermissions.ACCESS_MOVE_AND_RESIZE_TEXT,
    ]);
};

export const getIsUserAllowedAccessToMoveAndResizeIcons = (state: RootState) => {
    return isUserAllowedToUseAdvancedPermission(state, [
        UserPermissions.ACCESS_MOVE_AND_RESIZE_ICONS,
    ]);
};

export const getIsUserAllowedAccessToMoveAndResizeCaptions = (state: RootState) => {
    return isUserAllowedToUseAdvancedPermission(state, [
        UserPermissions.ACCESS_MOVE_AND_RESIZE_CAPTIONS,
    ]);
};

/** Team size selectors */

export function getTeamPurchasedSeats(state: RootState) {
    return state.account.team_settings.purchased_seats;
}

export function getTeamPurchasedSeatCeiling(state: RootState) {
    return state.account.team_settings.purchased_seat_ceiling;
}

export function getTeamMaxTeamSize(state: RootState) {
    return state.account.team_settings.max_team_size;
}

export function getCanTranslateVideos(state: RootState) {
    return state.account.team_settings.can_translate_videos;
}

// Is self serve and has no seats left
export function getShouldRequireSeatPurchase(state: RootState) {
    const isEnterprise = getServerData()?.USER?.is_enterprise_plan;
    const hasRemainingSeats =
        getTeamSize(state) + getPendingInvitationCount(state) < getTeamMaxTeamSize(state);
    return !isEnterprise && !hasRemainingSeats;
}

// Is enteprise and has no seats left
export function enterpriseNoOpenSeats(state: RootState) {
    const isEnterprise = getServerData()?.USER?.is_enterprise_plan;
    const hasRemainingSeats =
        getTeamSize(state) + getPendingInvitationCount(state) < getTeamMaxTeamSize(state);
    return isEnterprise && !getServerData()?.USER?.unlimited_invitations && !hasRemainingSeats;
}

/**
 * Invitations
 */

export function selectPendingInvitationIds(state: RootState): Immutable.List<number> {
    const orm = new StoreORM<TeamInvitation>(state.account.invitations);
    return orm.getAllIds();
}

export function selectPendingInvitationById(
    state: RootState,
    id: number,
): ImmutableMap<TeamInvitation> | undefined {
    const orm = new StoreORM<TeamInvitation>(state.account.invitations);
    return orm.getByIdOrUndefined(id);
}

export function getPendingInvitationCount(state: RootState) {
    const orm = new StoreORM<TeamInvitation>(state.account.invitations);
    return orm.length;
}

export function isInvitationsLoading(state: RootState) {
    return state.account.invitationsIsLoading;
}

/** Account selectors */

export function getAccountEmail(state: RootState) {
    return state.account.user_profile.email;
}

export function getAccountLoadingStatus(state: RootState) {
    return state.account.user_profile.loading;
}

export function getAccountPhotoUrl(state: RootState) {
    return state.account.user_profile.photo_url;
}

export function getIsUserStaff(state: RootState) {
    return state.account.user_profile.is_staff;
}

/**
 * SSO selectors
 */

export function getCanSeeSSOSettings(state: RootState) {
    return state.account.team_settings.can_see_sso_settings;
}

export function getSSOIdPConfig(state: RootState) {
    return state.account.team_settings.sso_idp_config;
}

export function getSSOLoginStatus(state: RootState) {
    return state.account.team_settings.sso_login_status;
}

export function getAutoBlueprintRecommendationsEnabled(state: RootState): boolean {
    return state.account.team_settings.auto_blueprint_recommendations_enabled ?? true;
}

export function getDefaultDashboardView(state: RootState) {
    return state.account.team_settings.default_dashboard_view ?? DefaultDashboardViewEnum.CREATE;
}

export function getGenxPresetRestrictionEnabled(state: RootState): boolean {
    return state.account.team_settings.genx_preset_restriction_enabled ?? false;
}

export function getAllowedGenxPresetSlugs(state: RootState): string[] {
    return state.account.team_settings.allowed_genx_preset_slugs;
}
